import { useEffect, useRef, createContext, useContext, useCallback } from "react";
import { useImmer } from "use-immer";
import { io } from "socket.io-client";
import { useUser, getToken, signOut } from '@/systems/Authentication';

const SyncManagerContext = createContext();

const socket_url = import.meta.env.DEV ? "http://127.0.0.1:8842" : "https://database-sync-io.sapientclinician.com";

export const SyncManagerProvider = ({ children }) => {

  const { isCompleteUserdata } = useUser();

  const database_callbacks = useRef([]);//(true,undefined) || ("error",error) || ("logged_out",undefined)
  const socket = useRef(null);

  const open_db_listeners = useRef({});
  const outstanding_one_time_reads = useRef({});
  const send_order = useRef(0);
  const receive_order = useRef(0);
  const receive_queue = useRef([]);

  // useEffect(() => {
  //   let int = setInterval(() => {
  //     console.log("open_db_listeners", open_db_listeners.current);
  //   }, 10000);
  //   return () => {
  //     clearInterval(int);
  //   }
  // }, []);

  const getNextSendOrder = useCallback(() => {
    send_order.current++;
    return send_order.current - 1;
  }, []);

  // Not supported with no_deltas_callback or resume_info
  const restoreListener = useCallback((query, sync_timestamp) => {
    if (socket.current?.connected) {
      var sync_id = JSON.stringify([query, sync_timestamp]);
      if (open_db_listeners.current[sync_id]) {
        open_db_listeners.current[sync_id].has_initial_response = false;
        socket.current.emit("read", { query: query, sync: true, sync_timestamp: sync_timestamp, order: getNextSendOrder() }, function (response) {
          if (open_db_listeners.current[sync_id] && !open_db_listeners.current[sync_id].has_handled_initial_response) {
            open_db_listeners.current[sync_id].handleInitialResponse(response);
          } else {
            if (response.success) {//Calculate changes (delta) and send appropriate events
              if (open_db_listeners.current[sync_id]) {
                open_db_listeners.current[sync_id].has_initial_response = true;
                var was_any_change = false;

                //Adds
                for (let i = 0; i < response.response.value.length; i++) {
                  if (open_db_listeners.current[sync_id] && !open_db_listeners.current[sync_id].value[response.response.value[i]["_id"]]) {
                    open_db_listeners.current[sync_id].value[response.response.value[i]["_id"]] = response.response.value[i];
                    if (open_db_listeners.current[sync_id].child_added) {
                      open_db_listeners.current[sync_id].child_added({ id: response.response.value[i]["_id"], value: response.response.value[i] });
                    }
                    if (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].changed_single) {
                      open_db_listeners.current[sync_id].changed_single(response.response.value[i]);
                    }
                    was_any_change = true;
                  }
                }

                var new_response_ids = response.response.value.map(function (a) { return a["_id"] });

                //Deletes
                if (open_db_listeners.current[sync_id]) {
                  for (let i = 0; i < Object.keys(open_db_listeners.current[sync_id].value).length; i++) {
                    var old_id = Object.keys(open_db_listeners.current[sync_id].value)[i];
                    if (!(new_response_ids.indexOf(old_id) > -1)) {
                      delete open_db_listeners.current[sync_id].value[old_id];
                      if (open_db_listeners.current[sync_id].child_removed) {
                        open_db_listeners.current[sync_id].child_removed({ id: old_id, value: null });
                      }
                      if (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].changed_single) {
                        open_db_listeners.current[sync_id].changed_single(null);
                      }
                      was_any_change = true;
                    }
                    if (!open_db_listeners.current[sync_id]) {
                      break;
                    }
                  }
                }

                //Changes (Modifies)
                if (open_db_listeners.current[sync_id]) {
                  for (let i = 0; i < response.response.value.length; i++) {
                    var this_id = response.response.value[i]["_id"];
                    if (!compareDatabaseObjects(open_db_listeners.current[sync_id].value[this_id], response.response.value[i])) {
                      open_db_listeners.current[sync_id].value[this_id] = response.response.value[i];
                      if (open_db_listeners.current[sync_id].child_changed) {
                        open_db_listeners.current[sync_id].child_changed({ id: this_id, value: response.response.value[i] });
                      }
                      if (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].changed_single) {
                        open_db_listeners.current[sync_id].changed_single(response.response.value[i]);
                      }
                      was_any_change = true;
                    }
                    if (!open_db_listeners.current[sync_id]) {
                      break;
                    }
                  }
                }

                if (was_any_change && open_db_listeners.current[sync_id]) {
                  if (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].changed) {
                    open_db_listeners.current[sync_id].changed(open_db_listeners.current[sync_id].value);
                  }
                }

                while (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].pending_after_initial_response.length > 0) {
                  (open_db_listeners.current[sync_id].pending_after_initial_response.splice(0, 1)[0])();
                }
              }
            } else {
              if (open_db_listeners.current[sync_id]) {
                if (open_db_listeners.current[sync_id].error) {
                  open_db_listeners.current[sync_id].error();
                  if (open_db_listeners.current[sync_id]) {
                    delete open_db_listeners.current[sync_id];
                  }
                }
              }
            }
          }
        });
      }
    }
  }, [getNextSendOrder]);

  const retryOutstandingOneTimeRead = useCallback((query, sync_timestamp) => {
    if (socket.current?.connected) {
      var sync_id = JSON.stringify([query, sync_timestamp]);
      if (outstanding_one_time_reads.current[sync_id]) {
        socket.current.emit("read", { query: query, sync: false, sync_timestamp: sync_timestamp, order: getNextSendOrder() }, function (response) {
          if (outstanding_one_time_reads.current[sync_id]) {
            outstanding_one_time_reads.current[sync_id].handleInitialResponse(response);
          }
        });
      }
    }
  }, [getNextSendOrder]);

  const databaseConnect = useCallback(() => {

    if (socket.current?.connected) {

      while (database_callbacks.current.length > 0) {
        (database_callbacks.current.pop())(true);
      }

    } else {

      if (isCompleteUserdata) {

        getToken().then(function (idToken) {

          if (socket.current) {

            socket.current.io.opts.auth.token = idToken;
            socket.current.connect();

          } else {

            socket.current = io(socket_url, {
              path: "/socket/",
              withCredentials: true,
              auth: {
                token: idToken
              },
              reconnectionDelay: 300,
              randomizationFactor: 0.25
            });

            socket.current.on("connect", () => {

              send_order.current = 0;
              receive_order.current = 0;
              receive_queue.current = [];

              console.log("Connected");

              Object.keys(open_db_listeners.current).forEach(key => {
                var this_listener = open_db_listeners.current[key];
                if (this_listener) {
                  if (this_listener.no_deltas_callback) {
                    var pend_callback = this_listener.no_deltas_callback;
                    delete open_db_listeners.current[key];
                    pend_callback();
                  } else {
                    restoreListener(this_listener.query, this_listener.sync_timestamp);
                  }
                }
              });

              Object.keys(outstanding_one_time_reads.current).forEach(key => {
                var this_outstanding_read = outstanding_one_time_reads.current[key];
                if (this_outstanding_read) {
                  retryOutstandingOneTimeRead(this_outstanding_read.query, this_outstanding_read.sync_timestamp);
                }
              });

              while (database_callbacks.current.length > 0) {
                (database_callbacks.current.pop())(true);
              }

            });

            socket.current.on("connect_error", (error) => {

              if (error && error.message == "Auth failed") {
                console.log("Auth failed");
                signOut();
              } else {
                databaseConnect();
              }

            });

            socket.current.on("disconnect", (reason) => {
              console.log("Disconnected: " + reason);
              if (reason === "io client disconnect") {
                while (database_callbacks.current.length > 0) {
                  (database_callbacks.current.pop())("logged_out");
                }
              } else {
                databaseConnect();
              }
            });

            const handleSyncEvent = (data) => {
              if (data.order == receive_order.current) {
                receive_order.current++;
                var sync_id = JSON.stringify([data.query, data.sync_timestamp]);
                if (open_db_listeners.current[sync_id]) {
                  const handleEvent = () => {
                    if (open_db_listeners.current[sync_id]) {
                      const forwardEvent = () => {
                        if (open_db_listeners.current[sync_id][data.event_name]) {
                          open_db_listeners.current[sync_id][data.event_name](data.event_data);//{id: ..., value: ...}
                        }
                        if (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].changed_single) {
                          open_db_listeners.current[sync_id].changed_single(data.event_data.value);
                        }
                        if (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].changed) {
                          open_db_listeners.current[sync_id].changed(open_db_listeners.current[sync_id].value);
                        }
                      }
                      if (data.event_name == "child_added") {
                        if (open_db_listeners.current[sync_id].no_deltas_callback) {
                          forwardEvent();
                        } else {
                          if (!open_db_listeners.current[sync_id].value[data.event_data.id]) {
                            open_db_listeners.current[sync_id].value[data.event_data.id] = data.event_data.value;
                            forwardEvent();
                          }
                        }
                      }
                      if (data.event_name == "child_changed") {
                        if (open_db_listeners.current[sync_id].no_deltas_callback) {
                          forwardEvent();
                        } else {
                          if (open_db_listeners.current[sync_id].value[data.event_data.id]) {
                            open_db_listeners.current[sync_id].value[data.event_data.id] = data.event_data.value;
                            forwardEvent();
                          }
                        }
                      }
                      if (data.event_name == "child_removed") {
                        if (open_db_listeners.current[sync_id].no_deltas_callback) {
                          forwardEvent();
                        } else {
                          if (open_db_listeners.current[sync_id].value[data.event_data.id]) {
                            delete open_db_listeners.current[sync_id].value[data.event_data.id];
                            forwardEvent();
                          }
                        }
                      }
                      if (data.event_name == "error") {
                        if (open_db_listeners.current[sync_id].error) {
                          open_db_listeners.current[sync_id].error();
                        }
                        if (open_db_listeners.current[sync_id]) {
                          delete open_db_listeners.current[sync_id];
                        }
                      }
                      if (data.event_name == "closed") {
                        if (open_db_listeners.current[sync_id].no_deltas_callback) {
                          var pend_callback = open_db_listeners.current[sync_id].no_deltas_callback;
                          delete open_db_listeners.current[sync_id];
                          pend_callback();
                        } else {
                          restoreListener(open_db_listeners.current[sync_id].query, open_db_listeners.current[sync_id].sync_timestamp);
                        }
                      }
                    }
                  }
                  if (open_db_listeners.current[sync_id].has_initial_response && !open_db_listeners.current[sync_id].paused) {
                    handleEvent();
                  } else {
                    open_db_listeners.current[sync_id].pending_after_initial_response.push(handleEvent);
                  }
                }
                if (receive_queue.current[receive_order.current]) {
                  (receive_queue.current.splice(receive_order.current, 1)[0])();
                }
              } else {
                receive_queue.current[data.order] = function () {
                  handleSyncEvent(data);
                }
              }
            }
            socket.current.on("sync", handleSyncEvent);

            const handleReauthenticate = (data) => {
              if (data.order == receive_order.current) {
                receive_order.current++;

                getToken().then(function (idToken) {

                  socket.emit("reauthenticate", {
                    order: getNextSendOrder(),
                    token: idToken
                  }, function (response) {
                    if (!response.success) {
                      console.error(new Error(response.error));
                    }
                  });

                }).catch(function(error) {
                  console.error(error);
                });

              } else {
                receive_queue.current[data.order] = function () {
                  handleSyncEvent(data);
                }
              }
            }
            socket.current.on("reauthenticate", handleReauthenticate);

          }

        }).catch(function (error) {

          while (database_callbacks.current.length > 0) {
            (database_callbacks.current.pop())("error", error);
          }

        });

      } else {

        while (database_callbacks.current.length > 0) {
          (database_callbacks.current.pop())("logged_out");
        }

      }

    }

  }, [isCompleteUserdata, getNextSendOrder, retryOutstandingOneTimeRead, restoreListener]);

  const databaseDisconnect = useCallback(() => {

    if (socket.current) {
      socket.current.disconnect();
    }

  }, []);

  useEffect(() => {
    if (isCompleteUserdata) {
      databaseConnect();
    } else {
      databaseDisconnect();
    }
  }, [isCompleteUserdata, databaseConnect, databaseDisconnect]);

  useEffect(() => {
    return () => {
      databaseDisconnect();
    }
  }, [databaseDisconnect]);

  // If no_deltas_callback is provided, disables filtering of events based on previous values (like skipping a delete event for an item that wasn't in initial read)
  // no_deltas_callback is a callback function for if the listener gets disconnected.
  // If no_deltas_callback is provided, the listener will get disabled upon disconnection.
  // no_deltas_callback is intended for use when the change events might include events outside the scope of the initial request, like how news only requests 15 posts, but still wants to receive change events for all posts.
  // resume_info must be used when calling databaseRead in direct response to a change event to avoid a temporary loop where the event gets delivered again every time until enough time passes (about 5-10 seconds). This isn't compatible with delta stuff (so no_deltas_callback is required with this option). A loop should be impossible if no_deltas_callback is not used, because the delta calculation would suppress the duplicate event, and databaseRead shouldn't ever be called in direct response to a change event.
  const databaseRead = useCallback(({query, enable_listener, single_document_mode, no_deltas_callback, resume_info}) => {

    var sync_timestamp = Date.now();

    var sync_id = JSON.stringify([query, sync_timestamp]);

    function handleInitialResponse(response, resolve, reject) {

      if (enable_listener && open_db_listeners.current[sync_id]) {
        open_db_listeners.current[sync_id].has_handled_initial_response = true;
      }

      if (Object.keys(open_db_listeners.current).length > 5) {
        console.warn("More than 5 open listeners! They are: " + Object.keys(open_db_listeners.current).join(", "));
      }

      if (!enable_listener) {
        if (outstanding_one_time_reads.current[sync_id]) {
          delete outstanding_one_time_reads.current[sync_id];
        } else {
          return;//Don't handle if one time read already resolved
        }
      }

      if (response.success) {//It is guaranteed that this initial response comes in before any change events
        resolve(single_document_mode ? (response.response.value.length == 1 ? response.response.value[0] : null) : response.response);
        if (enable_listener && open_db_listeners.current[sync_id]) {
          open_db_listeners.current[sync_id].has_initial_response = true;
          if (!open_db_listeners.current[sync_id].no_deltas_callback) {
            open_db_listeners.current[sync_id].value = {};
            for (let i = 0; i < response.response.value.length; i++) {
              open_db_listeners.current[sync_id].value[response.response.value[i]["_id"]] = response.response.value[i];
            }
          }
          while (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].pending_after_initial_response.length > 0) {
            (open_db_listeners.current[sync_id].pending_after_initial_response.splice(0, 1)[0])();
          }
        }
      } else {
        reject(new Error(response.error + ", query: " + JSON.stringify(query)));
        if (enable_listener && open_db_listeners.current[sync_id]) {
          open_db_listeners.current[sync_id].off();
        }
      }

    }

    var resolvePromise;
    var rejectPromise;

    var readPromise = new Promise(function (resolve, reject) {

      resolvePromise = resolve;
      rejectPromise = reject;

      database_callbacks.current.push(function (success, error) {
        if (success) {

          socket.current.emit("read", { query: query, sync: enable_listener || false, sync_timestamp: sync_timestamp, resume_info: resume_info, order: getNextSendOrder() }, function (response) { handleInitialResponse(response, resolve, reject) });

        } else {
          reject(error);
        }
      });

      databaseConnect();

    });

    var sync_object = {
      query: query,
      sync_timestamp: sync_timestamp,
      has_initial_response: false,
      has_handled_initial_response: false,
      handleInitialResponse: function (response) { handleInitialResponse(response, resolvePromise, rejectPromise) },
      pending_after_initial_response: [],
      value: {},
      paused: false,
      pause: function () {
        if (open_db_listeners.current[sync_id]) {
          open_db_listeners.current[sync_id].paused = true;
        }
      },
      unpause: function () {
        if (open_db_listeners.current[sync_id]) {
          open_db_listeners.current[sync_id].paused = false;
          while (open_db_listeners.current[sync_id] && open_db_listeners.current[sync_id].pending_after_initial_response.length > 0) {
            (open_db_listeners.current[sync_id].pending_after_initial_response.splice(0, 1)[0])();
          }
        }
      },
      off: function () {
        if (open_db_listeners.current[sync_id]) {
          if (socket.current?.connected) {

            socket.current.emit("stop_sync", { query: query, sync_timestamp: sync_timestamp, order: getNextSendOrder() });

          }

          delete open_db_listeners.current[sync_id];
        }
      },
      child_added: null,
      child_changed: null,
      child_removed: null,
      error: null,
      changed_single: null,
      changed: null,
      no_deltas_callback: no_deltas_callback
    }

    if (enable_listener) {
      open_db_listeners.current[sync_id] = sync_object;
    } else {
      outstanding_one_time_reads.current[sync_id] = {
        query: query,
        sync_timestamp: sync_timestamp,
        handleInitialResponse: function (response) { handleInitialResponse(response, resolvePromise, rejectPromise) }
      }
    }

    return enable_listener ? { initial: readPromise, sync: sync_object } : { initial: readPromise };

  }, [databaseConnect, getNextSendOrder]);

  function compareDatabaseObjects(object1, object2) {

    // Get all keys for non-null entries
    var keys1 = Object.keys(object1);
    var keys2 = Object.keys(object2);
    for (let i = keys1.length - 1; i > -1; i--) {
      if (!(object1[keys1[i]] != null)) {
        keys1.splice(i, 1);
      }
    }
    for (let i = keys2.length - 1; i > -1; i--) {
      if (!(object2[keys2[i]] != null)) {
        keys2.splice(i, 1);
      }
    }

    if (keys1.length !== keys2.length) {
      return false;
    }

    // Compare all non-null entry keys using JSON.stringify
    for (let i = 0; i < keys1.length; i++) {
      if (object2[keys1[i]] != null) {

        if (JSON.stringify(object1[keys1[i]]) !== JSON.stringify(object2[keys1[i]])) {
          return false;
        }

      } else {
        return false;
      }
    }

    return true;
  }

  return (
    <SyncManagerContext.Provider value={{ databaseRead }}>
      {children}
    </SyncManagerContext.Provider>
  )

}

export const useDatabaseSync = ({ query, original_value, enabled = true, single_document_mode = false, merge = true }) => {
  const { databaseRead } = useContext(SyncManagerContext);
  const [value, updateValue] = useImmer(original_value);
  const query_string = JSON.stringify(query);
  
  useEffect(() => {

    if (enabled) {

      var read_result = databaseRead({ query: JSON.parse(query_string), enable_listener: true, single_document_mode: single_document_mode });
      read_result.initial.then(function(response) {
        if (single_document_mode) {
          if (response && merge) {
            updateValue((draft) => {
              Object.keys(response).forEach((key) => { 
                draft[key] = response[key];
              });
            });
          } else {
            updateValue(response || null);
          }
        } else {
          updateValue(response.value);
        }
      }).catch(function(error) {
        console.error(error);
      })
      read_result.sync.error = function(error) {
        console.error(error);
      }
      if (single_document_mode) {
        read_result.sync.changed_single = function(value) {
          if (merge) {
            if (value) {
              updateValue((draft) => {
                Object.keys(value).forEach((key) => { 
                  draft[key] = value[key];
                });
              });
            } else {
              updateValue(null);
            }
          } else {
            updateValue(value);
          }
        }
      } else {
        read_result.sync.changed = function(value) {
          updateValue(Object.values(value));
        }
      }

      return () => {
        read_result.sync.off();
      }

    }

  }, [databaseRead, query_string, single_document_mode, merge, updateValue, enabled]);

  return value;
}

export function useFunctionalDatabaseSync() {
  const { databaseRead } = useContext(SyncManagerContext);

  const databaseSync = useCallback(({ query, single_document_mode = false, onChanged }) => {

    var read_result = databaseRead({ query, enable_listener: true, single_document_mode: single_document_mode });
    read_result.initial.then(function(response) {
      if (single_document_mode) {
        onChanged(response || null);
      } else {
        onChanged(response.value);
      }
    }).catch(function(error) {
      console.error(error);
    })
    read_result.sync.error = function(error) {
      console.error(error);
    }
    if (single_document_mode) {
      read_result.sync.changed_single = function(value) {
        onChanged(value);
      }
    } else {
      read_result.sync.changed = function(value) {
        onChanged(Object.values(value));
      }
    }

    return {
      off: () => {
        read_result.sync.off();
      }
    }

  }, [databaseRead]);

  return { databaseSync };
}